import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { getShippingRates, previewPaymentTotals } from '../services/api';
import './Cart.css';

const EMPTY_ADDRESS = { street: '', city: '', state: '', zip: '', country: 'US' };
const EMPTY_PARCEL = { length: '', width: '', height: '', weightLb: '', weightOz: '' };

function getParcelWeightOz(parcel) {
  const pounds = Number(parcel.weightLb || 0);
  const ounces = Number(parcel.weightOz || 0);

  if (!Number.isFinite(pounds) || !Number.isFinite(ounces) || pounds < 0 || ounces < 0) {
    return null;
  }

  const total = (pounds * 16) + ounces;
  return total > 0 ? total : null;
}

function hasDestination(address) {
  return Boolean(address.street && address.city && address.state && address.zip);
}

function hasAnyParcelDimension(parcel) {
  return Boolean(parcel.length || parcel.width || parcel.height || parcel.weightLb || parcel.weightOz);
}

function hasCompleteParcelDimensions(parcel) {
  return Boolean(parcel.length && parcel.width && parcel.height && getParcelWeightOz(parcel));
}

export default function Cart() {
  const {
    items,
    savedItems,
    removeItem,
    removeSavedItem,
    updateQty,
    moveToSaved,
    moveSavedToCart,
    subtotal,
    shippingQuote,
    saveShippingQuote,
    clearShippingQuote,
  } = useCart();
  const navigate = useNavigate();
  const shippingPanelRef = useRef(null);
  const itemCount = items.reduce((sum, item) => sum + item.qty, 0);
  const [address, setAddress] = useState(() => shippingQuote?.address || EMPTY_ADDRESS);
  const [parcel, setParcel] = useState(() => shippingQuote?.parcel || EMPTY_PARCEL);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingError, setShippingError] = useState('');
  const [checkoutError, setCheckoutError] = useState('');
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxError, setTaxError] = useState('');
  const selectedRate = shippingQuote?.stale ? null : shippingQuote?.selectedRate || null;
  const shippingCost = selectedRate?.rate || 0;
  const taxAmount = shippingQuote?.stale ? 0 : Number(shippingQuote?.taxAmount || 0);
  const total = subtotal + shippingCost + taxAmount;
  const customParcelStarted = hasAnyParcelDimension(parcel);
  const customParcelReady = hasCompleteParcelDimensions(parcel);
  const parcelWeightOz = getParcelWeightOz(parcel);
  const canEstimateShipping = hasDestination(address);

  if (items.length === 0 && savedItems.length === 0) {
    return (
      <div className="container" style={{ padding: '3rem 1.5rem' }}>
        <div className="cart-empty card">
          <div className="cart-empty-icon">🛍️</div>
          <h2>Your cart is empty</h2>
          <p>Browse the latest listings and add a few standouts before heading to checkout.</p>
          <Link to="/" className="btn btn-primary">Browse Items</Link>
        </div>
      </div>
    );
  }

  const updateAddress = (key, value) => {
    const nextAddress = { ...address, [key]: value };
    setAddress(nextAddress);
    saveShippingQuote({ address: nextAddress, parcel, rates: [], selectedRate: null });
  };

  const updateParcel = (key, value) => {
    const nextParcel = { ...parcel, [key]: value };
    setParcel(nextParcel);
    saveShippingQuote({ address, parcel: nextParcel, rates: [], selectedRate: null });
  };

  const clearParcel = () => {
    setParcel(EMPTY_PARCEL);
    saveShippingQuote({ address, parcel: EMPTY_PARCEL, rates: [], selectedRate: null });
    setShippingError('');
  };

  const fetchRates = async () => {
    if (customParcelStarted && !customParcelReady) {
      setShippingError('Enter length, width, height, and a package weight in pounds/ounces to use a custom package profile.');
      return;
    }

    setShippingLoading(true);
    setShippingError('');
    setCheckoutError('');

    try {
      const { data } = await getShippingRates({
        toAddress: {
          name: 'Cart Estimate',
          street: address.street,
          city: address.city,
          state: address.state,
          zip: address.zip,
          country: address.country || 'US',
        },
        items: items.map((item) => ({ id: item.id, qty: item.qty })),
        parcel: customParcelReady
          ? {
              length: parcel.length,
              width: parcel.width,
              height: parcel.height,
              weight: parcelWeightOz,
            }
          : undefined,
      });

      const selected = data.rates.find((rate) => rate.id === shippingQuote?.selectedRate?.id) || data.rates[0] || null;
      if (selected) {
        await previewTotals(selected, data.rates);
      } else {
        saveShippingQuote({
          address,
          parcel,
          rates: data.rates,
          selectedRate: null,
          taxAmount: 0,
          taxRate: 0,
          total: subtotal,
          totalsPreviewed: false,
        });
      }
    } catch (err) {
      setShippingError(err.response?.data?.error || 'Could not fetch live shipping rates for this cart.');
      clearShippingQuote();
      saveShippingQuote({ address, parcel, rates: [], selectedRate: null, taxAmount: 0, taxRate: 0, total: subtotal, totalsPreviewed: false });
    } finally {
      setShippingLoading(false);
    }
  };

  const previewTotals = async (rate, availableRates = shippingQuote?.rates || []) => {
    setTaxLoading(true);
    setTaxError('');

    try {
      const { data } = await previewPaymentTotals({
        items: items.map((item) => ({ id: item.id, qty: item.qty })),
        shippingCost: rate.rate,
        address: {
          street: address.street,
          city: address.city,
          state: address.state,
          zip: address.zip,
          country: address.country || 'US',
        },
      });

      saveShippingQuote({
        address,
        parcel,
        rates: availableRates,
        selectedRate: rate,
        taxAmount: data.taxAmount,
        taxRate: data.taxRate,
        total: data.total,
        totalsPreviewed: true,
      });
    } catch (err) {
      setTaxError(err.response?.data?.error || 'Could not estimate taxes for this order yet.');
      saveShippingQuote({
        address,
        parcel,
        rates: availableRates,
        selectedRate: rate,
        taxAmount: 0,
        taxRate: 0,
        total: subtotal + rate.rate,
        totalsPreviewed: false,
      });
    } finally {
      setTaxLoading(false);
    }
  };

  const chooseRate = async (rate) => {
    await previewTotals(rate);
  };

  const handleProceedToCheckout = () => {
    if (!selectedRate) {
      setCheckoutError('Choose a live shipping rate before continuing to checkout.');
      shippingPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    setCheckoutError('');
    navigate('/checkout');
  };

  return (
    <div className="container" style={{ padding: '2rem 1.5rem' }}>
      <div className="page-header">
        <h1>Shopping Cart</h1>
        <p>{itemCount} item{itemCount !== 1 ? 's' : ''} ready for checkout</p>
      </div>

      <div className="cart-layout">
        <div className="cart-items">
          <div className="cart-callout card">
            <strong>Almost there.</strong>
            <p>Enter the delivery address here to see live UPS, USPS, and FedEx rates before you head to payment.</p>
          </div>

          {items.map(item => (
            <div key={item.id} className="cart-item">
              <div className="cart-item-image">
                {item.image ? (
                  <img src={item.image} alt={item.title} />
                ) : (
                  <div className="cart-no-image">📦</div>
                )}
              </div>
              <div className="cart-item-info">
                <Link to={`/product/${item.id}`} className="cart-item-title">{item.title}</Link>
                <p className="cart-item-price">${item.price.toFixed(2)} each</p>
                <span className="cart-stock-note">Max available: {item.maxQty}</span>
              </div>
              <div className="cart-item-qty-wrap">
                <span className="cart-mobile-label">Qty</span>
                <div className="cart-item-qty">
                  <button onClick={() => updateQty(item.id, item.qty - 1)}>−</button>
                  <span>{item.qty}</span>
                  <button onClick={() => updateQty(item.id, Math.min(item.maxQty, item.qty + 1))}>+</button>
                </div>
              </div>
              <div className="cart-item-total-wrap">
                <span className="cart-mobile-label">Total</span>
                <div className="cart-item-total">${(item.price * item.qty).toFixed(2)}</div>
              </div>
              <button className="cart-remove" onClick={() => removeItem(item.id)}>✕</button>
              <button className="cart-save-btn" onClick={() => moveToSaved(item.id)}>
                Save for later
              </button>
            </div>
          ))}

          {savedItems.length > 0 && (
            <div className="saved-items card">
              <div className="saved-items-header">
                <div>
                  <strong>Saved for later</strong>
                  <p>Keep favorites here without losing them.</p>
                </div>
                <span>{savedItems.length} item{savedItems.length === 1 ? '' : 's'}</span>
              </div>

              <div className="saved-items-list">
                {savedItems.map((item) => (
                  <div key={item.id} className="saved-item-row">
                    <div className="saved-item-copy">
                      <strong>{item.title}</strong>
                      <div className="saved-item-meta">
                        <span>${Number(item.price).toFixed(2)}</span>
                        {item.on_sale && (
                          <span className="saved-item-sale">{item.sale_percent}% off</span>
                        )}
                      </div>
                    </div>
                    <div className="saved-item-actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => moveSavedToCart(item.id)}>
                        Move to cart
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => removeSavedItem(item.id)}>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="cart-summary card">
          <h2>Order Summary</h2>
          <div className="cart-summary-pill">{itemCount} item{itemCount !== 1 ? 's' : ''}</div>
          {items.length > 0 ? (
            <>
          <div className="cart-shipping-panel" ref={shippingPanelRef}>
            <div className="cart-shipping-header">
              <div>
                <strong>Delivery estimate</strong>
                <p>Rates update for this cart and address.</p>
              </div>
              {shippingQuote?.stale && <span className="cart-shipping-stale">Needs refresh</span>}
            </div>

            <div className="cart-address-grid">
              <input
                className="form-control"
                placeholder="Street address"
                value={address.street}
                onChange={(e) => updateAddress('street', e.target.value)}
              />
              <input
                className="form-control"
                placeholder="City"
                value={address.city}
                onChange={(e) => updateAddress('city', e.target.value)}
              />
              <input
                className="form-control"
                placeholder="State"
                maxLength={2}
                value={address.state}
                onChange={(e) => updateAddress('state', e.target.value.toUpperCase())}
              />
              <input
                className="form-control"
                placeholder="ZIP"
                value={address.zip}
                onChange={(e) => updateAddress('zip', e.target.value)}
              />
            </div>

            <div className="cart-box-panel">
              <div className="cart-box-copy">
                <strong>Box dimensions</strong>
                <p>Optional. Enter the package size and packed weight if you want rates based on your exact shipping box.</p>
              </div>
              {customParcelStarted && (
                <button
                  type="button"
                  className="cart-box-reset"
                  onClick={clearParcel}
                >
                  Use saved item package details instead
                </button>
              )}
              <div className="cart-box-grid">
                <label className="cart-box-field">
                  <span>Length (in)</span>
                  <input
                    className="form-control"
                    placeholder="12"
                    inputMode="decimal"
                    value={parcel.length}
                    onChange={(e) => updateParcel('length', e.target.value)}
                  />
                </label>
                <label className="cart-box-field">
                  <span>Width (in)</span>
                  <input
                    className="form-control"
                    placeholder="9"
                    inputMode="decimal"
                    value={parcel.width}
                    onChange={(e) => updateParcel('width', e.target.value)}
                  />
                </label>
                <label className="cart-box-field">
                  <span>Height (in)</span>
                  <input
                    className="form-control"
                    placeholder="3"
                    inputMode="decimal"
                    value={parcel.height}
                    onChange={(e) => updateParcel('height', e.target.value)}
                  />
                </label>
                <label className="cart-box-field cart-box-field-wide">
                  <span>Weight (lb / oz)</span>
                  <div className="cart-weight-grid">
                    <label className="cart-weight-field">
                      <span>Pounds</span>
                      <input
                        className="form-control"
                        placeholder="0"
                        inputMode="decimal"
                        value={parcel.weightLb}
                        onChange={(e) => updateParcel('weightLb', e.target.value)}
                      />
                    </label>
                    <label className="cart-weight-field">
                      <span>Ounces</span>
                      <input
                        className="form-control"
                        placeholder="8"
                        inputMode="decimal"
                        value={parcel.weightOz}
                        onChange={(e) => updateParcel('weightOz', e.target.value)}
                      />
                    </label>
                  </div>
                </label>
              </div>
              {customParcelStarted && !customParcelReady && (
                <p className="cart-box-hint">
                  Finish all box fields, including pounds/ounces, or clear them to use the item&apos;s saved package profile.
                </p>
              )}
            </div>

            <button
              className="btn btn-secondary btn-block"
              onClick={fetchRates}
              disabled={!canEstimateShipping || shippingLoading}
            >
              {shippingLoading ? 'Checking live carrier rates...' : 'Get live shipping rates'}
            </button>

            {shippingError && <div className="alert alert-error cart-shipping-alert">{shippingError}</div>}

            {taxError && <div className="alert alert-error cart-shipping-alert">{taxError}</div>}

            {shippingQuote?.stale && (
              <div className="alert alert-info cart-shipping-alert">
                Your cart changed after the last quote. Refresh rates to keep shipping costs accurate.
              </div>
            )}

            {!!shippingQuote?.rates?.length && (
              <div className="cart-rate-list">
                {shippingQuote.rates.map((rate) => (
                  <label key={rate.id} className={`cart-rate-option ${selectedRate?.id === rate.id ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="cartShippingRate"
                      checked={selectedRate?.id === rate.id}
                      onChange={() => chooseRate(rate)}
                    />
                    <div className="cart-rate-info">
                      <strong>{rate.carrier}</strong>
                      <span>{rate.service}</span>
                    </div>
                    <div className="cart-rate-meta">
                      <strong>${rate.rate.toFixed(2)}</strong>
                      <span>{rate.days ? `${rate.days} day${rate.days > 1 ? 's' : ''}` : 'ETA varies'}</span>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="summary-row">
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          <div className="summary-row">
            <span>Shipping</span>
            <span className="text-muted">{selectedRate ? `$${shippingCost.toFixed(2)}` : 'Choose a live rate'}</span>
          </div>
          <div className="summary-row">
            <span>Estimated tax</span>
            <span className="text-muted">
              {selectedRate ? (taxLoading ? 'Calculating...' : `$${taxAmount.toFixed(2)}`) : 'Select shipping first'}
            </span>
          </div>
          <div className="summary-divider" />
          <div className="summary-row summary-total">
            <span>Total</span>
            <span>${total.toFixed(2)}{selectedRate ? '' : '+'}</span>
          </div>
          {checkoutError && <div className="alert alert-error cart-shipping-alert">{checkoutError}</div>}

          <div className="cart-checkout-cta">
            <button
              className="btn btn-primary btn-lg btn-block"
              onClick={handleProceedToCheckout}
              style={{ marginTop: '1rem' }}
            >
              {selectedRate ? 'Proceed to Checkout' : 'Choose Shipping to Continue'}
            </button>
          </div>
          <p className="cart-checkout-note">
            {!selectedRate
              ? 'Choose a delivery rate here before heading to checkout.'
              : 'Your selected shipping rate will carry into checkout and payment.'}
          </p>
            </>
          ) : (
            <div className="cart-empty-summary">
              <strong>Your cart is empty right now.</strong>
              <p>Move one of your saved items back into the cart when you’re ready to check out.</p>
            </div>
          )}
          <Link to="/" className="continue-shopping">← Continue Shopping</Link>
        </div>
      </div>
    </div>
  );
}
